// Voice provider abstraction for Spinhouse Roleplay (Phase 2).
//
// MVP uses the browser Web Speech API (no keys, Chrome/Edge, tr-TR). The same
// interface is what a future Deepgram (STT) + ElevenLabs (TTS) backend bridge
// plugs into — swap the implementation, keep the call sites in RoleplayPage.
//
//   createSTT(lang) -> { supported, start({onInterim,onFinal,onError,onEnd}), stop() }
//   createTTS(lang) -> { supported, speak(text, onEnd), cancel(), warmVoices() }
//
// Sentence chunking lives in the page (it feeds speak() one sentence at a time
// so the buyer "starts talking" before the full reply has streamed).

const DEFAULT_LANG = "tr-TR";

export function sttSupported() {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}

export function ttsSupported() {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

export function createSTT(lang = DEFAULT_LANG) {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return { supported: false, start: () => {}, stop: () => {} };

  let rec = null;

  // Continuous recognition + our OWN silence timer, so a thinking pause does
  // not end the turn early (the browser's native endpointing is too eager).
  // End-of-turn fires only after SILENCE_MS of quiet AFTER the user has spoken.
  const SILENCE_MS = 1600;
  let silenceTimer = null;

  const start = ({ onInterim, onFinal, onError, onEnd }) => {
    stop();
    rec = new SR();
    rec.lang = lang;
    rec.interimResults = true;
    rec.continuous = true;
    rec.maxAlternatives = 1;
    let finalText = "";
    let spoke = false;

    const armSilence = () => {
      clearTimeout(silenceTimer);
      silenceTimer = setTimeout(() => { try { rec && rec.stop(); } catch { /* noop */ } }, SILENCE_MS);
    };

    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t;
        else interim += t;
      }
      if (interim && onInterim) onInterim(interim);
      spoke = true;
      armSilence(); // reset the quiet countdown on any speech
    };
    rec.onspeechstart = () => { spoke = true; armSilence(); };
    rec.onerror = (e) => { if (onError) onError(e.error || "stt_error"); };
    rec.onend = () => {
      clearTimeout(silenceTimer);
      // Browser may fire onend before we've spoken (its own VAD) — if so and the
      // user hasn't spoken yet, surface empty so the panel just goes idle.
      const t = finalText.trim();
      if (t && onFinal) onFinal(t);
      if (onEnd) onEnd(t);
      rec = null;
      void spoke;
    };
    try { rec.start(); } catch (e) { if (onError) onError(String(e)); }
  };

  const stop = () => {
    clearTimeout(silenceTimer);
    if (rec) { try { rec.stop(); } catch { /* noop */ } }
  };

  return { supported: true, start, stop };
}

export function createTTS(lang = DEFAULT_LANG) {
  if (!ttsSupported()) {
    return { supported: false, speak: (_t, cb) => cb && cb(), cancel: () => {}, warmVoices: () => {} };
  }
  let voice = null;
  const pickVoice = () => {
    const vs = window.speechSynthesis.getVoices() || [];
    voice =
      vs.find((v) => v.lang === lang) ||
      vs.find((v) => (v.lang || "").toLowerCase().startsWith("tr")) ||
      null;
  };
  pickVoice();
  // Voices load async in Chrome — repick when they arrive.
  if (typeof window.speechSynthesis.onvoiceschanged !== "undefined") {
    window.speechSynthesis.onvoiceschanged = pickVoice;
  }

  const speak = (text, onEnd) => {
    if (!text || !text.trim()) { onEnd && onEnd(); return; }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang;
    if (voice) u.voice = voice;
    u.rate = 1.0;
    u.onend = () => onEnd && onEnd();
    u.onerror = () => onEnd && onEnd();
    window.speechSynthesis.speak(u);
  };

  const cancel = () => { try { window.speechSynthesis.cancel(); } catch { /* noop */ } };

  // Some browsers need a user-gesture warm-up before the first utterance plays.
  const warmVoices = () => pickVoice();

  return { supported: true, speak, cancel, warmVoices };
}

// ---- Server STT path (Deepgram): record one mic utterance to a Blob ----
// Push-to-talk: start() opens the mic, stop() finalizes and resolves the audio
// Blob, which the page POSTs to /roleplay/sessions/{id}/transcribe.
export function recorderSupported() {
  return typeof navigator !== "undefined" &&
    !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) &&
    typeof window !== "undefined" && "MediaRecorder" in window;
}

export function createRecorder() {
  let mr = null;
  let chunks = [];
  let stream = null;
  // Voice-activity detection (so the user doesn't press a button to send).
  let audioCtx = null;
  let analyser = null;
  let vadTimer = null;

  // RMS thresholds (0..1). Speech is well above silence; tuned for laptop mics.
  const SPEAK_RMS = 0.018;
  const SILENCE_MS = 1500;   // pause this long after speaking → auto-stop (tolerates thinking)
  const MAX_MS = 25000;      // hard cap on one utterance

  const teardownVad = () => {
    if (vadTimer) { clearInterval(vadTimer); vadTimer = null; }
    if (audioCtx) { try { audioCtx.close(); } catch { /* noop */ } audioCtx = null; }
    analyser = null;
  };

  // onSpeechEnd fires once when the user goes quiet after having spoken.
  const start = async ({ onSpeechEnd } = {}) => {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    chunks = [];
    const mime =
      window.MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" :
      window.MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" :
      window.MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
    mr = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    mr.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    mr.start();

    if (onSpeechEnd) {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        audioCtx = new AC();
        const src = audioCtx.createMediaStreamSource(stream);
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 2048;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.fftSize);
        const startedAt = Date.now();
        let hasSpoken = false;
        let lastVoice = Date.now();
        let fired = false;
        vadTimer = setInterval(() => {
          if (!analyser) return;
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
          const rms = Math.sqrt(sum / buf.length);
          const now = Date.now();
          if (rms > SPEAK_RMS) { hasSpoken = true; lastVoice = now; }
          const tooLong = now - startedAt > MAX_MS;
          const silentAfterSpeech = hasSpoken && now - lastVoice > SILENCE_MS;
          if (!fired && (silentAfterSpeech || tooLong)) {
            fired = true;
            teardownVad();
            onSpeechEnd();
          }
        }, 100);
      } catch { /* VAD optional — fall back to manual stop */ }
    }
  };

  const stop = () => new Promise((resolve) => {
    teardownVad();
    const cleanup = () => { if (stream) stream.getTracks().forEach((t) => t.stop()); stream = null; };
    if (!mr || mr.state === "inactive") { cleanup(); return resolve(null); }
    mr.onstop = () => {
      const type = mr.mimeType || "audio/webm";
      const blob = new Blob(chunks, { type });
      cleanup();
      mr = null;
      resolve(blob);
    };
    try { mr.stop(); } catch { cleanup(); resolve(null); }
  });

  const abort = () => {
    teardownVad();
    try { if (mr && mr.state !== "inactive") mr.stop(); } catch { /* noop */ }
    if (stream) stream.getTracks().forEach((t) => t.stop());
    stream = null; mr = null; chunks = [];
  };

  return { start, stop, abort };
}

// Plays server-returned audio (ElevenLabs mp3 Blob) sequentially. Page fetches
// /roleplay/tts (auth header) and feeds Blobs here; onEnd fires per clip.
export function createAudioPlayer() {
  let audio = null;
  const speak = (blob, onEnd) => {
    cancel();
    const url = URL.createObjectURL(blob);
    audio = new Audio(url);
    const done = () => { URL.revokeObjectURL(url); onEnd && onEnd(); };
    audio.onended = done;
    audio.onerror = done;
    audio.play().catch(done);
  };
  const cancel = () => { if (audio) { try { audio.pause(); } catch { /* noop */ } audio = null; } };
  return { speak, cancel };
}

// Split a growing buffer into [completeSentences[], remainder]. Keeps the
// trailing partial sentence so we don't speak half a word mid-stream.
export function splitSentences(buffer) {
  const sentences = [];
  let rest = buffer;
  const re = /[^.!?…]*[.!?…]+["')\]]*\s*/g;
  let m;
  let lastIdx = 0;
  while ((m = re.exec(buffer)) !== null) {
    sentences.push(m[0].trim());
    lastIdx = re.lastIndex;
  }
  rest = buffer.slice(lastIdx);
  return [sentences.filter(Boolean), rest];
}
